import { useState, useEffect } from 'react';
import TopNav from './TopNav';
import { useToast } from '../Toast';

const ROLES = ['admin', 'bar_manager', 'bartender', 'barista', 'coffee_manager', 'production', 'sales', 'hr', 'kitchen_manager', 'cook', 'distilling'];

const ROLE_LABELS = {
  admin: 'Admin', bar_manager: 'Bar Manager', bartender: 'Bartender', barista: 'Barista',
  coffee_manager: 'Coffee Manager', production: 'Production', sales: 'Sales', hr: 'HR',
  kitchen_manager: 'Kitchen Manager', cook: 'Cook', distilling: 'Distilling',
};

// Tool grouping for the permissions page (ordered). Uncategorized slugs fall to 'Other'.
const CATEGORIES = [
  { key: 'bar',        label: 'Bar' },
  { key: 'coffee',     label: 'Coffee' },
  { key: 'kitchen',    label: 'Kitchen' },
  { key: 'production', label: 'Production & Brewery' },
  { key: 'sales',      label: 'Sales' },
  { key: 'admin',      label: 'Admin & HR' },
  { key: 'other',      label: 'Other' },
];

const TOOL_CATEGORY = {
  'cocktail-keeper': 'bar', 'bar-checklist': 'bar', 'spirits-ordering': 'bar', 'spirits-inventory': 'bar',
  'taproom-inventory': 'bar', 'taproom-inspections': 'bar', 'distro-taproom-orders': 'bar',
  'coffee-keeper': 'coffee', 'coffee-checklist': 'coffee', 'coffee-site': 'coffee', 'coffee-inventory': 'coffee',
  'recipes': 'kitchen', 'foodlab-checklist': 'kitchen',
  'production-schedule': 'production', 'production-weekly': 'production', 'production-photos': 'production',
  'packaging-log': 'production', 'label-inventory': 'production', 'tank-maintenance': 'production',
  'equipment-manuals': 'production',
  'sales-crm': 'sales',
  'hr-documents': 'admin', 'sops': 'admin', '86ed-customers': 'admin', 'user-management': 'admin',
  'incident-reports': 'admin', 'onboarding': 'admin', 'event-planner': 'other',
};

function Toggle({ active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`w-8 h-5 rounded-full transition-colors duration-200 relative ${active ? 'bg-orange-500' : 'bg-gray-600'}`}
    >
      <span className={`absolute top-0.5 w-3.5 h-3.5 bg-white rounded-full transition-all duration-200 ${active ? 'left-4' : 'left-0.5'}`} />
    </button>
  );
}

function Permissions({ onBack, onHome }) {
  const [tools, setTools] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selectedRole, setSelectedRole] = useState('bartender');
  const [error, setError] = useState('');
  const toast = useToast();

  const fetchData = async () => {
    try {
      const [toolsRes, permsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL || ''}/api/tools`, { credentials: 'include' }),
        fetch(`${process.env.REACT_APP_API_URL || ''}/api/permissions`, { credentials: 'include' }),
      ]);
      const toolsData = toolsRes.ok ? await toolsRes.json() : [];
      const permsData = permsRes.ok ? await permsRes.json() : [];
      // Guard against error-object responses — otherwise .filter/.some below throw
      // and the global ErrorBoundary drops the app into a reload loop.
      setTools(Array.isArray(toolsData) ? toolsData : []);
      setPermissions(Array.isArray(permsData) ? permsData : []);
      setError(permsRes.ok ? '' : (permsRes.status === 403
        ? 'You don’t have access to manage role permissions. Ask an admin to grant “Manage” on the User Management tool.'
        : 'Could not load permissions. Please try again.'));
    } catch {
      setTools([]); setPermissions([]);
      setError('Could not load permissions. Please try again.');
    }
  };

  useEffect(() => { fetchData(); }, []);

  const hasPermission = (role, tool_id, permission_level = 'view') =>
    permissions.some(p => p.role === role && p.tool_id === tool_id && p.permission_level === permission_level);

  const togglePermission = async (role, tool_id, permission_level = 'view') => {
    try {
      const method = hasPermission(role, tool_id, permission_level) ? 'DELETE' : 'POST';
      const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/permissions`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ role, tool_id, permission_level }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || 'Could not update permission.');
      fetchData();
    } catch (e) { toast.error(e.message); }
  };

  const grouped = CATEGORIES
    .map(cat => ({ ...cat, catTools: tools.filter(t => (TOOL_CATEGORY[t.slug] || 'other') === cat.key) }))
    .filter(g => g.catTools.length);

  // Quick summary of what the selected role currently has
  const accessCount = tools.filter(t => hasPermission(selectedRole, t.id, 'view')).length;
  const manageCount = tools.filter(t => hasPermission(selectedRole, t.id, 'upload')).length;

  return (
    <div className="min-h-screen bg-gray-900">
      <TopNav />

      <main className="px-6 py-10 max-w-3xl mx-auto">
        <div className="mb-6 text-center">
          <h2 className="text-cream text-4xl font-bold">Role Permissions</h2>
          <p className="text-gray-400 mt-2">Pick a role, then set what it can access and manage</p>
        </div>

        {error ? (
          <div className="max-w-md mx-auto text-center text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-xl px-5 py-4">
            {error}
          </div>
        ) : (
        <>
        {/* Role selector */}
        <div className="flex flex-wrap gap-2 justify-center mb-6">
          {ROLES.map(role => (
            <button
              key={role}
              onClick={() => setSelectedRole(role)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
                selectedRole === role ? 'text-white' : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'
              }`}
              style={selectedRole === role ? { backgroundColor: '#F05A28' } : undefined}
            >
              {ROLE_LABELS[role] || role}
            </button>
          ))}
        </div>

        {/* Context line for the selected role */}
        <div className="mb-6 text-center text-sm text-gray-400">
          {selectedRole === 'admin' ? (
            <span className="inline-block bg-gray-800 border border-gray-700 rounded-lg px-4 py-2">
              Admin can access every tool regardless of these toggles.
            </span>
          ) : (
            <span>
              <span className="text-white font-medium">{ROLE_LABELS[selectedRole]}</span> can access{' '}
              <span className="text-white font-medium">{accessCount}</span> {accessCount === 1 ? 'tool' : 'tools'}
              {manageCount > 0 && <> · manage <span className="text-white font-medium">{manageCount}</span></>}
            </span>
          )}
        </div>

        {/* Column headers */}
        <div className="flex items-center justify-end gap-8 px-4 mb-2 text-gray-500 text-[11px] uppercase tracking-wider">
          <span className="w-14 text-center">Access</span>
          <span className="w-14 text-center">Manage</span>
        </div>

        {grouped.map(cat => (
          <div key={cat.key} className="mb-6">
            <h3 className="text-orange-400 text-xs font-semibold uppercase tracking-wider px-1 mb-2">{cat.label}</h3>
            <div className="bg-gray-800 rounded-xl border border-gray-700 divide-y divide-gray-700/60">
              {cat.catTools.map(tool => {
                const canView = hasPermission(selectedRole, tool.id, 'view');
                return (
                  <div key={tool.id} className="flex items-center justify-between px-4 py-3">
                    <span className={`text-sm ${canView ? 'text-white' : 'text-gray-400'}`}>{tool.name}</span>
                    <div className="flex items-center gap-8">
                      <div className="w-14 flex justify-center">
                        <Toggle
                          active={canView}
                          onClick={() => togglePermission(selectedRole, tool.id, 'view')}
                        />
                      </div>
                      <div className="w-14 flex justify-center">
                        <Toggle
                          active={hasPermission(selectedRole, tool.id, 'upload')}
                          onClick={() => togglePermission(selectedRole, tool.id, 'upload')}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        </>
        )}
      </main>
    </div>
  );
}

export default Permissions;
